/**
 * The edit-lock indicator (contracts-plan.md §8.5, R25; §11 tab 4). Presentational
 * only — WorkspaceTab.tsx owns the 30 s poll (heartbeat while editable, a plain
 * GET while not — no SSE channel exists for `contracts.lock.changed` today, see
 * this WP's report NOTES [OWNER]) and passes the current lock/holder/editable
 * down as props.
 */
import { Lock, Unlock } from 'lucide-react';
import { outlineBtnCls } from '../../../ui';
import type { WorkspaceHolder, WorkspaceLockInfo } from '../../api/workspace';

interface Props {
  lock: WorkspaceLockInfo | null;
  holder: WorkspaceHolder | null;
  editable: boolean;
  /** §4.2: breaking somebody else's lock is reserved to administrator/super_admin. */
  canBreakLock: boolean;
  breaking: boolean;
  onBreakLock: () => void;
}

export function LockIndicator({ lock, holder, editable, canBreakLock, breaking, onBreakLock }: Props) {
  const text = editable
    ? 'You hold the lock'
    : holder
      ? `Being edited by ${holder.name ?? 'another user'}`
      : lock?.expired
        ? 'Lock expired'
        : 'Read-only';

  return (
    <div data-testid="ws-lock" className="flex items-center gap-2">
      <span
        title={
          editable
            ? `Heartbeat keeps this lock alive (§8.5, ${lock?.ttl_seconds ?? 300}s TTL).`
            : holder
              ? `Locked by ${holder.name ?? holder.user_id} (§8.5).`
              : 'No active lock.'
        }
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
          editable
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
        }`}
      >
        {editable ? <Unlock size={12} /> : <Lock size={12} />}
        {text}
      </span>
      {!editable && holder && canBreakLock && (
        <button
          type="button"
          data-testid="ws-break-lock"
          className={outlineBtnCls()}
          disabled={breaking}
          onClick={onBreakLock}
          title="Break this lock (administrator only, audited)"
        >
          Break lock
        </button>
      )}
    </div>
  );
}

export default LockIndicator;
